import { makeObservable, observable, action, toJS } from 'mobx';
import apolloClient from 'libs/apollo/apollo.lib';
import { gql } from '@apollo/client';
import { plural, singular } from 'pluralize';
import { capitalize } from '../libs/helpers/helper.lib';

import layoutStore from './layout.store';

class BaseObjectStore {
  isLoading = false;
  objects = [];
  searchText = '';
  query = {};
  orderBy = {};
  extraSearchArgs = [];
  fields = ` `;
  first = 10;
  page = 1;
  paginatorInfo = null;
  recentCounts = {};
  object = null;
  draftObject = {};
  name = null;
  abortControllers = [];

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      setIsLoading: action,
      objects: observable,
      setObjects: action,
      searchText: observable,
      setSearchText: action,
      query: observable,
      setQuery: action,
      orderBy: observable,
      setOrderBy: action,
      extraSearchArgs: observable,
      setExtraSearchArgs: action,
      fields: observable,
      setFields: action,
      first: observable,
      setFirst: action,
      page: observable,
      setPage: action,
      paginatorInfo: observable,
      setPaginationInfo: action,
      recentCounts: observable,
      setRecentCounts: action,
      object: observable,
      setObject: action,
      draftObject: observable,
      setDraftObject: action
    });
  }

  async createObject() {
    const draftObject = this.getDraftObject();
    const fields = this.getFields();
    const singularName = this.getSingularName();
    const singularNameCapitalize = this.getSingularCapitalizeName();
    let createdObject;
    const mutation = gql`
      mutation($${singularName}: Create${singularNameCapitalize}Input!) {
        create${singularNameCapitalize}(${singularName}: $${singularName}) {
          ${fields}
        }
      }
    `;
    const variables = {
      [`${singularName}`]: draftObject
    };
    this.setIsLoading(true);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      if (response && response.data[`create${singularNameCapitalize}`]) {
        this.setDraftObject({});
        createdObject = response.data[`create${singularNameCapitalize}`];
        if (createdObject) {
          const existingObjects = this.getObjects();
          const updatedObjects = [createdObject, ...existingObjects];
          this.setObjects(updatedObjects);
          await apolloClient.resetStore();
        }
      } else {
        throw new Error(
          response.errors ? response.errors[0].message : 'Something went wrong!'
        );
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message ? error.message : `Error Creating ${this.name}`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
    return createdObject;
  }

  async updateObject() {
    const draftObject = this.getDraftObject();
    const fields = this.getFields();
    const singularName = this.getSingularName();
    const singularNameCapitalize = this.getSingularCapitalizeName();
    let updatedObject;
    const mutation = gql`
      mutation($${singularName}: Update${singularNameCapitalize}Input!) {
        update${singularNameCapitalize}(${singularName}: $${singularName}) {
          ${fields}
        }
      }
    `;
    const variables = {
      [`${singularName}`]: draftObject
    };
    this.setIsLoading(true);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      if (response && response.data[`update${singularNameCapitalize}`]) {
        this.setDraftObject({});
        updatedObject = response.data[`update${singularNameCapitalize}`];
        if (updatedObject) {
          const existingObjects = toJS(this.getObjects());
          const findIndex = existingObjects.findIndex(
            (object) => object.id === updatedObject.id
          );
          let updatedObjects;
          if (findIndex > -1) {
            updatedObjects = existingObjects;
            updatedObjects[findIndex] = updatedObject;
          } else {
            updatedObjects = [updatedObject, ...existingObjects];
          }
          this.setObjects(updatedObjects);
          await apolloClient.resetStore();
        }
      } else {
        throw new Error(
          response.errors ? response.errors[0].message : 'Something went wrong!'
        );
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message ? error.message : `Error updating ${this.name}`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
    return updatedObject;
  }

  async upsertObject() {
    const draftObject = this.getDraftObject();
    const fields = this.getFields();
    const singularName = this.getSingularName();
    const singularNameCapitalize = this.getSingularCapitalizeName();
    let updatedOrCreatedObject;
    const mutation = gql`
      mutation($${singularName}: Upsert${singularNameCapitalize}Input!) {
        upsert${singularNameCapitalize}(${singularName}: $${singularName}) {
          ${fields}
        }
      }
    `;
    const variables = {
      [`${singularName}`]: draftObject
    };
    this.setIsLoading(true);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      if (response && response.data[`upsert${singularNameCapitalize}`]) {
        this.setDraftObject({});
        updatedOrCreatedObject =
          response.data[`upsert${singularNameCapitalize}`];
        if (updatedOrCreatedObject) {
          const existingObjects = this.getObjects();
          const findIndex = existingObjects.findIndex(
            (object) => object.id === updatedOrCreatedObject.id
          );
          let updatedObjects;
          if (findIndex > -1) {
            updatedObjects = toJS(existingObjects);
            updatedObjects[findIndex] = updatedOrCreatedObject;
          } else {
            updatedObjects = [updatedOrCreatedObject, ...existingObjects];
          }
          this.setObjects(updatedObjects);
          await apolloClient.resetStore();
        }
      } else {
        throw new Error(
          response.errors ? response.errors[0].message : 'Something went wrong!'
        );
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message ? error.message : `Error upserting ${this.name}`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
    return updatedOrCreatedObject;
  }

  async deleteObject() {
    const draftObject = this.getDraftObject();
    const fields = this.getFields();
    const singularNameCapitalize = this.getSingularCapitalizeName();
    let deletedObject;
    const mutation = gql`
      mutation($id: ID!) {
        delete${singularNameCapitalize}(id: $id) {
          ${fields}
        }
      }
    `;
    const variables = {
      id: draftObject.id
    };
    this.setIsLoading(true);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      if (response && response.data[`delete${singularNameCapitalize}`]) {
        this.setDraftObject({});
        deletedObject = response.data[`delete${singularNameCapitalize}`];
        if (deletedObject) {
          const existingObjects = toJS(this.getObjects());
          const updatedObjects = existingObjects.filter(
            (existingObject) => existingObject.id !== deletedObject.id
          );
          this.setObjects(updatedObjects);
          await apolloClient.resetStore();
        }
      } else {
        throw new Error(
          response.errors ? response.errors[0].message : 'Something went wrong!'
        );
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message ? error.message : `Error deleting ${this.name}`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
    return deletedObject;
  }

  async getObjectById(id) {
    const fields = this.getFields();
    const singularName = this.getSingularName();

    const query = gql`
      query ($id: ID!) {
        ${singularName}ById(id: $id) {
          ${fields}
        }
      }`;

    const variables = {
      id: id
    };
    this.setIsLoading(true);
    try {
      const response = await apolloClient.query({ query, variables });
      if (response && response.networkStatus === 7) {
        this.setObject(response.data[`${singularName}ById`]);
      } else {
        throw new Error('Something went wrong!');
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message
          ? error.message
          : `Error fetching ${this.name} by id`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
  }

  async searchObjects() {
    const fields = this.getFields();
    const args = this.getExtraSearchArgs();
    const pluralName = this.getPluralName();
    let keyTypeValues = '';
    let keysArgs = '';
    let variableArgs = {};
    args.forEach((arg) => {
      let keyTypeValue = '';
      let keyArg = '';
      for (const key in arg) {
        keyTypeValue = `, $${key}: WhereConditions`;
        keyArg = `, ${key}:$${key}`;
        variableArgs[key] = arg[key];
      }
      keyTypeValues += keyTypeValue;
      keysArgs += keyArg;
    });

    const orderBy = this.getOrderBy();
    const transformedOrderBy = [];
    for (const key in orderBy) {
      if (Object.hasOwnProperty.call(orderBy, key)) {
        transformedOrderBy.push({
          column: key,
          order: orderBy[key]
        });
      }
    }
    const query = gql`
      query(
          $query: WhereConditions,
          $orderBy: [OrderByClause!],
          $first: Int!,
          $page: Int!
          ${keyTypeValues}
        ) {
          ${pluralName}(query: $query, orderBy: $orderBy, first: $first, page: $page ${keysArgs}) {
            paginatorInfo {
              count
              currentPage
              firstItem
              hasMorePages
              lastItem
              lastPage
              perPage
              total
            }
            data {
              ${fields}
            }
          }
        }
      `;
    const variables = {
      query: this.getQuery(),
      orderBy: transformedOrderBy,
      first: this.getFirst(),
      page: this.getPage(),
      ...variableArgs
    };
    this.setIsLoading(true);
    try {
      const abortControllerLocal = new AbortController();
      this.addAbortController(abortControllerLocal);
      const response = await apolloClient.query({
        query,
        variables,
        context: {
          fetchOptions: {
            signal: abortControllerLocal.signal
          }
        }
      });
      if (response && response.networkStatus === 7) {
        this.setObjects(response.data[pluralName].data);
        this.setPaginationInfo(response.data[pluralName].paginatorInfo);
      } else {
        throw new Error('Something went wrong!');
      }
    } catch (error) {
      if (error.name !== 'Invariant Violation') {
        layoutStore.setToastConfig({
          message: error.message
            ? error.message
            : `Error fetching ${this.getPluralName()}`,
          color: 'danger'
        });
      }
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
  }

  async searchObjectsAll() {
    const fields = this.getFields();
    const args = this.getExtraSearchArgs();
    const pluralName = this.getPluralName();
    let keyTypeValues = '';
    let keysArgs = '';
    let variableArgs = {};
    args.forEach((arg) => {
      let keyTypeValue = '';
      let keyArg = '';
      for (const key in arg) {
        keyTypeValue = `, $${key}: WhereConditions`;
        keyArg = `, ${key}:$${key}`;
        variableArgs[key] = arg[key];
      }
      keyTypeValues += keyTypeValue;
      keysArgs += keyArg;
    });

    const orderBy = this.getOrderBy();
    const transformedOrderBy = [];
    for (const key in orderBy) {
      if (Object.hasOwnProperty.call(orderBy, key)) {
        transformedOrderBy.push({
          column: key,
          order: orderBy[key]
        });
      }
    }

    const query = gql`
      query(
          $query: WhereConditions,
          $orderBy: [OrderByClause!],
          ${keyTypeValues}
        ) {
            ${pluralName}All(query: $query, orderBy: $orderBy, ${keysArgs}) {
              ${fields}
            }
          }
      `;
    const variables = {
      query: this.getQuery(),
      orderBy: transformedOrderBy,
      ...variableArgs
    };
    this.setIsLoading(true);
    try {
      const abortControllerLocal = new AbortController();
      this.addAbortController(abortControllerLocal);
      const response = await apolloClient.query({
        query,
        variables,
        context: {
          fetchOptions: {
            signal: abortControllerLocal.signal
          }
        }
      });
      if (response && response.networkStatus === 7) {
        this.setObjects(response.data[`${pluralName}All`]);
      } else {
        throw new Error('Something went wrong!');
      }
    } catch (error) {
      layoutStore.setToastConfig({
        message: error.message
          ? error.message
          : `Error fetching ${this.getPluralName()}`,
        color: 'danger'
      });
      this.setIsLoading(false);
      throw error;
    }
    this.setIsLoading(false);
  }

  async fetchRecentCount() {
    this.setIsLoading(true);
    const query = gql`
      query ($name: String!) {
        recentCount(name: $name) {
          last24Hour
        }
      }
    `;
    const variables = {
      name: this.name
    };
    try {
      const response = await apolloClient.query({ query, variables });
      if (response && response.networkStatus === 7) {
        this.setRecentCounts(response.data.recentCount);
      } else {
        throw new Error('Something went wrong!');
      }
    } catch (error) {
      this.setIsLoading(false);
      throw error;
    }

    this.setIsLoading(false);
  }

  async handleOrderBy(key, fetchAll = false) {
    const existingOrderBy = this.getOrderBy();

    let updatedOrderBy = existingOrderBy;
    if (!existingOrderBy[key]) {
      updatedOrderBy = {
        ...updatedOrderBy,
        [`${key}`]: 'DESC'
      };
    } else if (existingOrderBy[key] === 'DESC') {
      updatedOrderBy[key] = 'ASC';
    } else if (existingOrderBy[key] === 'ASC') {
      delete updatedOrderBy[key];
    }
    this.setOrderBy(updatedOrderBy);
    try {
      if (fetchAll) {
        await this.searchObjectsAll();
      } else {
        await this.searchObjects();
      }
    } catch (error) {
      throw error;
    }
  }

  abortAllPendingRequests() {
    this.getAbortControllers().forEach((abortController) => {
      abortController.abort();
    });
  }

  addAbortController(abortController) {
    this.abortControllers.push(abortController);
  }

  getAbortControllers() {
    return this.abortControllers;
  }

  setAbortControllers(abortControllers) {
    this.abortControllers = abortControllers;
    return abortControllers;
  }

  getPluralName() {
    return plural(this.name);
  }

  getPluralCapitalizeName() {
    return capitalize(this.getPluralName());
  }

  getSingularName() {
    return singular(this.name);
  }

  getSingularCapitalizeName() {
    return capitalize(this.getSingularName());
  }

  setIsLoading(isLoading) {
    this.isLoading = isLoading;
    return this.isLoading;
  }

  getIsLoading() {
    return this.isLoading;
  }

  setObjects(objects) {
    this.objects = objects;
    return this.objects;
  }

  getObjects() {
    return this.objects;
  }

  setSearchText(searchText) {
    this.searchText = searchText;
    return this.searchText;
  }

  getSearchText() {
    return this.searchText;
  }

  setQuery(query) {
    this.query = query;
    return this.query;
  }

  getQuery() {
    return this.query;
  }

  setOrderBy(orderBy) {
    this.orderBy = orderBy;
    return this.orderBy;
  }

  getOrderBy() {
    return this.orderBy;
  }

  setExtraSearchArgs(extraSearchArgs) {
    this.extraSearchArgs = extraSearchArgs;
    return this.extraSearchArgs;
  }

  getExtraSearchArgs() {
    return this.extraSearchArgs;
  }

  setFields(fields) {
    this.fields = fields;
    return this.fields;
  }

  getFields() {
    return this.fields;
  }

  setFirst(first) {
    this.first = first;
    return this.first;
  }

  getFirst() {
    return this.first;
  }

  setPage(page) {
    this.page = page;
    return this.page;
  }

  getPage() {
    return this.page;
  }

  setPaginationInfo(paginationInfo) {
    this.paginationInfo = paginationInfo;
    return this.paginationInfo;
  }

  getPaginationInfo() {
    return this.paginationInfo;
  }

  setRecentCounts(recentCounts) {
    this.recentCounts = recentCounts;
    return this.recentCounts;
  }

  getRecentCounts() {
    return this.recentCounts;
  }

  setObject(object) {
    this.object = object;
    return this.object;
  }

  getObject() {
    return this.object;
  }

  setDraftObject(draftObject) {
    this.draftObject = draftObject;
    return this.draftObject;
  }

  getDraftObject() {
    return this.draftObject;
  }

  resetAllBaseFields() {
    this.setIsLoading(false);
    this.setObjects([]);
    this.setObject(null);
    this.setDraftObject({});
    this.setQuery({});
    this.setOrderBy({});
    this.setFirst(10);
    this.setPage(1);
    this.setPaginationInfo(null);
    this.setSearchText('');
    this.setExtraSearchArgs([]);
    this.setFields(` `);
    this.setAbortControllers([]);
  }
}

export default BaseObjectStore;
